// src/types/properties/PhotoTour.ts
var SPACED_CATEGORIES = [
  "bedroom" /* Bedroom */,
  "bathroom" /* Bathroom */
];
var isSpacedCategory = (category) => SPACED_CATEGORIES.includes(category);
var MAX_SPACE_INDEX = 20;
var normaliseSpaceIndex = (category, spaceIndex) => {
  if (!isSpacedCategory(category)) return null;
  if (typeof spaceIndex !== "number" || !Number.isInteger(spaceIndex)) return null;
  if (spaceIndex < 1 || spaceIndex > MAX_SPACE_INDEX) return null;
  return spaceIndex;
};
var PHOTO_TOUR_ORDER = [
  "cover" /* Cover */,
  "highlights" /* Highlights */,
  "general" /* General */,
  "living_room" /* LivingRoom */,
  "dining_area" /* DiningArea */,
  "kitchen" /* Kitchen */,
  "bedroom" /* Bedroom */,
  "bathroom" /* Bathroom */,
  "exterior" /* Exterior */,
  "backyard" /* Backyard */,
  "pool" /* Pool */,
  "patio" /* Patio */,
  "balcony" /* Balcony */,
  "neighbourhood" /* Neighbourhood */
];
var PHOTO_EDITOR_CATEGORIES = PHOTO_TOUR_ORDER.filter(
  (category) => category !== "highlights" /* Highlights */
);
var orderRank = (category) => {
  const rank = PHOTO_TOUR_ORDER.indexOf(category);
  return rank === -1 ? PHOTO_TOUR_ORDER.length : rank;
};
var LEAD_CATEGORIES = [
  "cover" /* Cover */,
  "highlights" /* Highlights */,
  "general" /* General */
];
var isLeadCategory = (category) => LEAD_CATEGORIES.includes(category);
var photoSectionKey = (category, spaceIndex) => `${category}#${spaceIndex ?? 0}`;
var parsePhotoSectionKey = (key) => {
  const separator = key.lastIndexOf("#");
  const category = separator === -1 ? key : key.slice(0, separator);
  const raw = separator === -1 ? "" : Number(key.slice(separator + 1));
  const spaceIndex = typeof raw === "number" && Number.isInteger(raw) ? raw : 0;
  return { category, spaceIndex: normaliseSpaceIndex(category, spaceIndex) };
};
function groupPhotosForTour(photos) {
  const sections = /* @__PURE__ */ new Map();
  for (const photo of photos) {
    const category = isLeadCategory(photo.category) ? "general" /* General */ : photo.category;
    const spaceIndex = isLeadCategory(photo.category) ? null : normaliseSpaceIndex(photo.category, photo.space_index);
    const key = photoSectionKey(category, spaceIndex);
    const existing = sections.get(key);
    if (existing) {
      existing.photos.push(photo);
    } else {
      sections.set(key, { key, category, space_index: spaceIndex, photos: [photo] });
    }
  }
  return [...sections.values()].sort(
    (a, b) => orderRank(a.category) - orderRank(b.category) || (a.space_index ?? 0) - (b.space_index ?? 0)
  );
}
function deriveSpaces({
  bedrooms,
  bathrooms,
  amenitySlugs = []
}) {
  const selected = new Set(amenitySlugs);
  const has = (...slugs) => slugs.some((slug) => selected.has(slug));
  const spaces = [
    { category: "living_room" /* LivingRoom */, spaceIndex: null },
    { category: "dining_area" /* DiningArea */, spaceIndex: null }
  ];
  if (has("kitchen" /* Kitchen */)) {
    spaces.push({ category: "kitchen" /* Kitchen */, spaceIndex: null });
  }
  for (let i = 1; i <= Math.min(bedrooms ?? 0, MAX_SPACE_INDEX); i++) {
    spaces.push({ category: "bedroom" /* Bedroom */, spaceIndex: i });
  }
  for (let i = 1; i <= Math.min(bathrooms ?? 0, MAX_SPACE_INDEX); i++) {
    spaces.push({ category: "bathroom" /* Bathroom */, spaceIndex: i });
  }
  if (has("private_backyard" /* PrivateBackyard */, "shared_backyard" /* SharedBackyard */)) {
    spaces.push({ category: "backyard" /* Backyard */, spaceIndex: null });
  }
  if (has("private_balcony" /* PrivateBalcony */)) {
    spaces.push({ category: "balcony" /* Balcony */, spaceIndex: null });
  }
  if (has("pool" /* Pool */)) {
    spaces.push({ category: "pool" /* Pool */, spaceIndex: null });
  }
  if (has(
    "outdoor_furniture" /* OutdoorFurniture */,
    "outdoor_dining" /* OutdoorDining */,
    "bbq_grill" /* BBQGrill */,
    "fire_pit" /* FirePit */,
    "sun_loungers" /* SunLoungers */
  )) {
    spaces.push({ category: "patio" /* Patio */, spaceIndex: null });
  }
  spaces.push(
    { category: "exterior" /* Exterior */, spaceIndex: null },
    { category: "neighbourhood" /* Neighbourhood */, spaceIndex: null }
  );
  return spaces.sort(
    (a, b) => orderRank(a.category) - orderRank(b.category) || (a.spaceIndex ?? 0) - (b.spaceIndex ?? 0)
  );
}

export {
  SPACED_CATEGORIES,
  isSpacedCategory,
  MAX_SPACE_INDEX,
  normaliseSpaceIndex,
  PHOTO_TOUR_ORDER,
  PHOTO_EDITOR_CATEGORIES,
  photoSectionKey,
  parsePhotoSectionKey,
  groupPhotosForTour,
  deriveSpaces
};
